import React from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useHomeScreen, type RecentActivityItem } from '@/hooks/residents/useHomeScreen';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useAppSelector } from '@/store';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { DesktopHomeViewSkeleton } from './DesktopHomeViewSkeleton';
import {
  GreetingSection,
  BalanceDueCard,
  QuickActionsSection,
  RecentActivitySection,
  CommunityResourcesSection,
  TodoCalendarSection,
  SectionHeader,
} from '@/components/residents/redesign';
import { LinkedAccountsCard } from '@/components';

const CONTENT_PADDING = REDESIGN_SPACING.horizontalPadding;
const SECTION_GAP = 24;
const INNER_GAP = 16;

export const DesktopHomeView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<StackNavigationProp<DesktopMainParamList>>();
  const { isTablet } = useResponsive();
  const isHomeLoading = useAppSelector((s) => s.home.isLoading);

  const {
    user,
    greeting,
    firstName,
    unitName,
    propertyName,
    recentActivity,
    linkedAccounts,
    newsfeedNewCount,
    unreadMessagesCount,
    surveysToAnswerCount,
    balanceDue,
    autopayEnabled,
    openTickets,
    refreshing,
    shouldShowPayments,
    handleRefresh,
  } = useHomeScreen();

  const leaseId = user?.currentLeaseId ? String(user.currentLeaseId) : '';
  const { accountCredit } = usePaymentBalance(leaseId);

  const handlePaymentsPress = () => {
    if (user?.currentLeaseId) {
      navigation.navigate('PaymentSummaryModal', {
        leaseId: user.currentLeaseId,
        residentId: user.residentId,
      });
    } else {
      navigation.navigate('Payments');
    }
  };
  const handleMaintenancePress = () => navigation.navigate('Maintenance');
  const handlePlannerPress = () => navigation.navigate('Planner');
  const handleBulletinsPress = () => navigation.navigate('CommunityBoard');
  const handleEventsPress = () => navigation.navigate('Events');
  const handleMarketplacePress = () => navigation.navigate('Marketplace');
  const handleResourcesPress = () => navigation.navigate('Resources');
  const handleLinkedAccountsPress = () => navigation.navigate('LinkedAccounts');
  const handleMessagesPress = () => navigation.navigate('Messages');
  const handleActivityItemPress = (item: RecentActivityItem) => {
    switch (item.type) {
      case 'maintenance':
        navigation.navigate('Maintenance');
        break;
      case 'payment':
        navigation.navigate('Payments');
        break;
      case 'bulletin':
        navigation.navigate('Bulletins');
        break;
    }
  };
  const handleWifiPress = () => navigation.navigate('Wifi');

  const handleSurveysPress = () => {
    navigation.navigate('Surveys', { showBackLink: true });
  };

  const useStackedLayout = isTablet;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      {isHomeLoading && !refreshing ? (
        <DesktopHomeViewSkeleton isTablet={isTablet} />
      ) : (
        <>
          <GreetingSection
            greeting={greeting}
            firstName={firstName}
            unitName={unitName}
          />

          <View style={useStackedLayout ? styles.stackedColumn : styles.twoColumnRow}>
            <View style={styles.columnPrimary}>
              <TodoCalendarSection onViewAll={handlePlannerPress} />
            </View>

            <View style={styles.columnSecondary}>
              <QuickActionsSection
                openTicketsCount={openTickets?.length || 0}
                newsfeedNewCount={newsfeedNewCount}
                unreadMessagesCount={unreadMessagesCount}
                surveysCount={surveysToAnswerCount}
                onMaintenance={handleMaintenancePress}
                onNewsfeed={handleBulletinsPress}
                onMessages={handleMessagesPress}
                onSurveys={handleSurveysPress}
              />
            </View>
          </View>

          <View style={useStackedLayout ? styles.stackedColumn : styles.twoColumnRow}>
            <View style={styles.columnPrimary}>
              {shouldShowPayments && (
                <>
                  <SectionHeader title="Payments" />
                  <BalanceDueCard
                    balanceDue={balanceDue}
                    accountCredit={accountCredit}
                    propertyName={propertyName}
                    unitName={unitName}
                    autopayEnabled={autopayEnabled}
                    onMakePayment={handlePaymentsPress}
                  />
                </>
              )}
              <RecentActivitySection
                items={recentActivity}
                onItemPress={handleActivityItemPress}
                maxItems={3}
              />
            </View>

            <View style={[styles.columnSecondary, styles.rightColumnGap]}>
              <CommunityResourcesSection
                onMarketplace={handleMarketplacePress}
                onResources={handleResourcesPress}
                onEvents={handleEventsPress}
                onWifi={handleWifiPress}
              />
              <LinkedAccountsCard
                linkedAccounts={linkedAccounts}
                onPress={handleLinkedAccountsPress}
              />
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    padding: CONTENT_PADDING,
    paddingBottom: 60,
    gap: SECTION_GAP,
  },
  twoColumnRow: {
    flexDirection: 'row',
    gap: INNER_GAP,
  },
  stackedColumn: {
    gap: INNER_GAP,
  },
  columnPrimary: {
    flex: 3,
  },
  columnSecondary: {
    flex: 2,
  },
  rightColumnGap: {
    gap: INNER_GAP,
  },
});

export default DesktopHomeView;
